import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  useTheme,
  alpha,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

interface Transaction {
  id: number;
  date: string;
  clientId: number;
  clientName: string;
  clientPseudo?: string;
  clientPhone?: string | number;
  amount: number;
  type: 'credit' | 'debit';
  status: 'paid' | 'unpaid';
  paidAmount: number;
  paidAt: string | null;
  paidBy: { id: number; username: string; } | null;
  remainingBalance: number;
  creditEntryId: number;
  createdAt: string;
  updatedAt: string;
}

interface MonthData {
  month: string;
  transactions: Transaction[];
  totalAmount: number;
  creditAmount: number;
  debitAmount: number;
  netAmount: number;
  paidCount: number;
  unpaidCount: number;
  days: Array<{
    date: string;
    transactions: Transaction[];
    totalAmount: number;
    creditAmount: number;
    debitAmount: number;
    netAmount: number;
    paidCount: number;
    unpaidCount: number;
  }>;
}

interface PDFReportProps {
  data: {
    summary: {
      totalTransactions: number;
      totalAmount: number;
      paidCount: number;
      unpaidCount: number;
      creditAmount: number;
      paidCreditAmount?: number;
      unpaidCreditAmount?: number;
      debitAmount: number;
      netAmount: number;
      clientCount: number;
      typeBreakdown: {
        credit: number;
        debit: number;
      };
    };
    data: MonthData[];
  };
  startDate: Date | null;
  endDate: Date | null;
  type: 'credits' | 'receipts';
}

export const PDFReport: React.FC<PDFReportProps> = ({
  data,
  startDate,
  endDate,
  type
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const getDaysSince = (dateValue: string | Date | null | undefined): number | null => {
    if (!dateValue) return null;
    const raw = dateValue instanceof Date ? dateValue : new Date(dateValue);
    if (Number.isNaN(raw.getTime())) return null;
    // Compare at local day precision to avoid timezone/hour drift
    const a = new Date(raw.getFullYear(), raw.getMonth(), raw.getDate()).getTime();
    const now = new Date();
    const b = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const diffMs = b - a;
    return Math.max(0, Math.floor(diffMs / (1000 * 60 * 60 * 24)));
  };

  const getAgeColors = (daysSince: number | null) => {
    if (daysSince === null) {
      return {
        border: alpha(theme.palette.grey[500], 0.45),
        bg: alpha(theme.palette.grey[500], 0.14),
        stripe: theme.palette.grey[500],
        text: theme.palette.text.primary,
      };
    }
    if (daysSince < 3) {
      return {
        border: alpha(theme.palette.success.main, 0.75),
        bg: alpha(theme.palette.success.main, 0.22),
        stripe: theme.palette.success.main,
        text: theme.palette.text.primary,
      };
    }
    if (daysSince <= 6) {
      return {
        border: alpha(theme.palette.warning.main, 0.75),
        bg: alpha(theme.palette.warning.main, 0.22),
        stripe: theme.palette.warning.main,
        text: theme.palette.text.primary,
      };
    }
    return {
      border: alpha(theme.palette.error.main, 0.75),
      bg: alpha(theme.palette.error.main, 0.22),
      stripe: theme.palette.error.main,
      text: theme.palette.text.primary,
    };
  };

  const summary = data?.summary || {
    totalTransactions: 0,
    totalAmount: 0,
    paidCount: 0,
    unpaidCount: 0,
    creditAmount: 0,
    debitAmount: 0,
    netAmount: 0,
    paidAmount: 0,
    unpaidAmount: 0,
    paidCreditAmount: 0,
    unpaidCreditAmount: 0
  };

  /** Month groups from API each have .transactions; without groupBy, data[] is flat transactions */
  const allTransactions: Transaction[] = Array.isArray(data?.data)
    ? (data.data as (MonthData | Transaction)[]).flatMap((chunk: MonthData | Transaction) => {
        if (chunk && typeof chunk === 'object' && 'transactions' in chunk && Array.isArray((chunk as MonthData).transactions)) {
          return (chunk as MonthData).transactions;
        }
        if (chunk && typeof chunk === 'object' && 'id' in chunk && 'amount' in chunk) {
          return [chunk as Transaction];
        }
        return [];
      })
    : [];

  return (
    <Box p={2} bgcolor="#fff">
      {/* Header */}
      <Box mb={2} textAlign="center">
        <Typography variant="h6" gutterBottom>
          {t(type === 'credits' ? 'Credits Report' : 'Receipts Report')}
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.8rem' }}>
          {startDate?.toLocaleDateString()} - {endDate?.toLocaleDateString()}
        </Typography>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} mb={2}>
        <Grid item xs={3}>
          <Paper sx={{ p: 1, bgcolor: alpha(theme.palette.success.main, 0.1) }}>
            <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.7rem' }}>
              {t('Total Credits')}
            </Typography>
            <Typography variant="body1" sx={{ fontSize: '0.9rem', fontWeight: 'bold' }}>
              {new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'TND',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
              }).format(summary.creditAmount)}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={3}>
          <Paper sx={{ p: 1, bgcolor: alpha(theme.palette.success.main, 0.1) }}>
            <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.7rem' }}>
              {t('Paid Credits')}
            </Typography>
            <Typography variant="body1" sx={{ fontSize: '0.9rem', fontWeight: 'bold', color: theme.palette.success.main }}>
              {new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'TND',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
              }).format(summary.paidCreditAmount ?? 0)}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={3}>
          <Paper sx={{ p: 1, bgcolor: alpha(theme.palette.warning.main, 0.1) }}>
            <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.7rem' }}>
              {t('Unpaid Credits')}
            </Typography>
            <Typography variant="body1" sx={{ fontSize: '0.9rem', fontWeight: 'bold', color: theme.palette.warning.main }}>
              {new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'TND',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
              }).format(summary.unpaidCreditAmount ?? 0)}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={3}>
          <Paper sx={{ p: 1, bgcolor: alpha(theme.palette.info.main, 0.1) }}>
            <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.7rem' }}>
              {t('Total Debits')}
            </Typography>
            <Typography variant="body1" sx={{ fontSize: '0.9rem', fontWeight: 'bold', color: theme.palette.info.main }}>
              {new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'TND',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
              }).format(summary.debitAmount)}
            </Typography>
          </Paper>
        </Grid>
      </Grid>
      {/* Transactions Grouped by Client */}
      <Typography variant="body2" sx={{ fontSize: '0.8rem', mb: 1, fontWeight: 'bold' }}>
        {t('Transactions by Client')}
      </Typography>
      {allTransactions.length > 0 ? (() => {
        const transactions = allTransactions;
        const clientGroups: Record<string, { 
          clientName: string; 
          clientPseudo?: string;
          clientPhone?: string | number;
          totalCredit: number;
          totalPaid: number;
          totalUnpaid: number;
          totalDebit: number;
          transactions: Transaction[] 
        }> = {};
        
        transactions.forEach((t: Transaction) => {
          const clientId = String(t.clientId || 'unknown');
          if (!clientGroups[clientId]) {
            clientGroups[clientId] = {
              clientName: t.clientName || 'Unknown',
              clientPseudo: t.clientPseudo,
              clientPhone: t.clientPhone,
              totalCredit: 0,
              totalPaid: 0,
              totalUnpaid: 0,
              totalDebit: 0,
              transactions: []
            };
          }
          clientGroups[clientId].transactions.push(t);
          if (t.type === 'credit') {
            clientGroups[clientId].totalCredit += t.amount;
            if (t.status === 'paid') {
              clientGroups[clientId].totalPaid += t.amount;
            } else {
              clientGroups[clientId].totalUnpaid += t.amount;
            }
          } else if (t.type === 'debit') {
            clientGroups[clientId].totalDebit += t.amount;
          }
        });
        
        return Object.values(clientGroups).map((client, idx) => (
          <Box key={idx} mb={2}>
            {/* Client Header */}
            <Paper
              sx={{
                p: 1,
                mb: 1,
                bgcolor: alpha(theme.palette.primary.main, 0.06),
                borderLeft: `4px solid ${theme.palette.primary.main}`,
              }}
            >
              {(() => {
                const oldestDaysSince = client.transactions.reduce<number | null>((acc, tx) => {
                  const d = getDaysSince(tx.date);
                  if (d === null) return acc;
                  if (acc === null) return d;
                  return Math.max(acc, d);
                }, null);
                const colors = getAgeColors(oldestDaysSince);
                return (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                      px: 1.25,
                      py: 0.75,
                      borderRadius: 2,
                      border: `1.5px solid ${colors.border}`,
                      borderLeft: `6px solid ${colors.stripe}`,
                      bgcolor: colors.bg,
                      color: colors.text,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      fontWeight={700}
                      sx={{
                        fontSize: '0.85rem',
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        minWidth: 0,
                      }}
                    >
                      {client.clientName} {client.clientPseudo && `(${client.clientPseudo})`}
                    </Typography>

                    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
                      <Typography variant="body2" fontWeight={700} color="text.secondary" sx={{ fontSize: '0.65rem', whiteSpace: "nowrap" }}>
                        {t('Phone')}: {client.clientPhone || '-'}
                      </Typography>
                      {oldestDaysSince !== null && (
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 800,
                            px: 0.85,
                            py: 0.2,
                            borderRadius: 999,
                            border: `1px solid ${alpha(colors.stripe, 0.7)}`,
                            bgcolor: alpha(colors.stripe, 0.12),
                            whiteSpace: "nowrap",
                            fontSize: "0.65rem",
                            lineHeight: 1.2,
                          }}
                        >
                          {oldestDaysSince}d
                        </Typography>
                      )}
                    </Box>
                  </Box>
                );
              })()}
              <Grid container spacing={1} mt={0.5}>
                <Grid item xs={3}>
                  <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.6rem' }}>
                    {t('Credit')}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.7rem', fontWeight: 'bold' }}>
                    {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'TND', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(client.totalCredit)}
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.6rem' }}>
                    {t('Paid')}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.7rem', fontWeight: 'bold', color: theme.palette.success.main }}>
                    {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'TND', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(client.totalPaid)}
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.6rem' }}>
                    {t('Unpaid')}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.7rem', fontWeight: 'bold', color: theme.palette.warning.main }}>
                    {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'TND', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(client.totalUnpaid)}
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.6rem' }}>
                    {t('Debit')}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.7rem', fontWeight: 'bold', color: theme.palette.info.main }}>
                    {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'TND', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(client.totalDebit)}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
            
            {/* Client Transactions */}
            {client.transactions.map((transaction: Transaction) => (
              <Paper
                key={transaction.id}
                sx={{
                  p: 1,
                  mb: 0.5,
                  ml: 1,
                  borderLeft: `3px solid ${transaction.type === 'debit'
                      ? theme.palette.info.main
                      : transaction.status === 'paid'
                        ? theme.palette.success.main
                        : theme.palette.warning.main
                    }`,
                }}
              >
                <Grid container spacing={1} alignItems="center">
                  <Grid item xs={2}>
                    <Typography variant="body2" sx={{ fontSize: '0.65rem' }}>
                      #{transaction.id}
                    </Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="body2" sx={{ fontSize: '0.65rem' }}>
                      {transaction.date ? new Date(transaction.date).toLocaleDateString() : '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={2}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: '0.65rem',
                        color: transaction.type === 'debit' ? theme.palette.info.main : 'inherit',
                      }}
                    >
                      {t(transaction.type)}
                    </Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="body2" sx={{ fontSize: '0.65rem', fontWeight: 'bold' }}>
                      {new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: 'TND',
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0
                      }).format(transaction.amount)}
                    </Typography>
                  </Grid>
                  <Grid item xs={2}>
                    {transaction.type === 'credit' && (
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: '0.55rem',
                          fontWeight: 'bold',
                          color: transaction.status === 'paid' ? 'success.main' : 'warning.main',
                          textAlign: 'center',
                        }}
                      >
                        {t(transaction.status)}
                      </Typography>
                    )}
                  </Grid>
                </Grid>
              </Paper>
            ))}
          </Box>
        ));
      })() : Array.isArray(data?.data) && data.data.length > 0 && allTransactions.length === 0 ? (
        <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
          {t('No transactions found')}
        </Typography>
      ) : Array.isArray(data?.data) && data.data.length === 0 ? (
        <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
          {t('No transactions found')}
        </Typography>
      ) : (
        <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'error.main' }}>
          {t('Error loading transactions')}
        </Typography>
      )}
    </Box>
  );
};
